/**
 * Admin users feature — queries + mutations.
 *
 * Scope-agnostic, mirroring the automation-accounts surface:
 *   Platform scope (tenantID === null) → /api/v1/admin-users
 *   Tenant   scope (tenantID = UUID)   → /api/v1/tenants/{tenant_id}/admin-users
 *
 * Both surfaces share the same wire shapes (see backend
 * pkg/api/admin/adminuser.go) and the same verbs. The backend has
 * NO cross-tenant admin list by design — a caller always resolves
 * to exactly one scope. `api-client.rewritePathForHost` rewrites the
 * tenant path to /api/tenant/v1/... on tenant hosts, so this single
 * path shape is correct regardless of which host the SPA runs on.
 *
 * Every mutation goes through withStepUp; reads do not. The one-time
 * enrollment token returned on invite is NEVER written to the query
 * cache — it lives only in the mutation's resolved promise.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, type ApiError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { collectPages } from "./collect-pages";
import { withStepUp } from "@/lib/with-step-up";
import {
  inviteAdminResponseSchema,
  platformAdminSchema,
  platformAdminsListResponseSchema,
  type InviteAdminInput,
  type InviteAdminResponse,
  type PlatformAdmin,
  type PlatformAdminsListResponse,
  type UpdatePlatformAdminRoleInput,
} from "./schemas";

/**
 * Build the REST base for the requested scope. Platform scope passes
 * `null`; tenant scope passes the tenant's UUID. Kept as a single
 * helper so call sites can't diverge on URL shape.
 */
function base(tenantID: string | null): string {
  if (tenantID === null) {
    return "/api/v1/admin-users";
  }
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/admin-users`;
}

/** Map a null/UUID scope to the registry's scope-string convention. */
export function scopeKey(tenantID: string | null): string {
  return tenantID === null ? "platform" : tenantID;
}

// ── Queries ──────────────────────────────────────────────────────────

export async function fetchAdmins(
  tenantID: string | null,
): Promise<PlatformAdminsListResponse> {
  const raw = await apiFetch<unknown>(base(tenantID));
  return platformAdminsListResponseSchema.parse(raw);
}

/**
 * EVERY admin, following the cursor to the end.
 *
 * fetchAdmins above reads ONE PAGE. The endpoint is keyset-paginated and clamps an
 * unset limit to 50 server-side (adminuser/repository.go), and `admin_users` holds
 * automation accounts alongside humans — so on a tenant with more than 50 admin
 * rows the first page can omit real people entirely.
 *
 * That is survivable for a LIST view, which is what the single-page fetch was
 * written for and where a reader can see there are more. It is not survivable for
 * the three consumers added with the owner control, each of which needs
 * COMPLETENESS to be correct:
 *
 *   - the owner picker, which would silently fail to offer an eligible colleague;
 *   - its "N admins are not listed" note, which would blame the eligibility rule
 *     for an absence actually caused by pagination — on the one control whose job
 *     is to explain absences rather than let them read as a bug;
 *   - the owner-name resolvers on two surfaces, where a missed admin makes a
 *     correctly-owned credential render as a raw id, the branch those files define
 *     as "we could not read who is accountable" — a different and wrong fact.
 *
 * BOUNDED BY A PAGE CAP, not by trust. Admins are tens per tenant in practice, but
 * "in practice" is what the previous version assumed in a comment; the loop stops
 * after maxPages and returns what it has rather than spinning on a server that
 * keeps handing back cursors.
 */
// 20 pages x the server's 50-row clamp = 1000 admins. A SAFETY VALVE, not a limit
// any real tenant is expected to meet — and when it fires the caller is TOLD, via
// the retained next_cursor, rather than handed a partial list that looks whole.
const maxAdminPages = 20;

export async function fetchAllAdmins(
  tenantID: string | null,
): Promise<PlatformAdminsListResponse> {
  const { items, truncated } = await collectPages(async (cursor) => {
    const url = cursor === undefined
      ? base(tenantID)
      : `${base(tenantID)}?cursor=${encodeURIComponent(cursor)}`;
    const page = platformAdminsListResponseSchema.parse(await apiFetch<unknown>(url));
    return { items: page.items, nextCursor: page.next_cursor };
  }, maxAdminPages);

  // next_cursor is PRESERVED as the truncation signal rather than blanked. Blanking
  // it would report "this is every admin" on the one path whose whole purpose is
  // completeness — the defect this function was written to fix, reintroduced at a
  // larger row count. Consumers that care (the owner picker) check it and say so.
  return { items, next_cursor: truncated ? TRUNCATED_CURSOR : undefined };
}

/**
 * A sentinel standing in for "the walk stopped early". The real cursor is not
 * carried forward: nothing resumes this walk, and handing back a live cursor would
 * invite a caller to try — this is a flag, and it is shaped like the field it
 * replaces so no consumer has to learn a second convention.
 */
export const TRUNCATED_CURSOR = "__truncated__";

/**
 * The admins list for surfaces that need EVERY admin — the owner picker and the
 * owner-name resolvers. Distinct query key from useAdminsQuery so the paged list
 * view and the complete list do not overwrite each other in the cache.
 */
export function useAllAdminsQuery(tenantID: string | null) {
  return useQuery<PlatformAdminsListResponse, ApiError>({
    queryKey: [...queryKeys.adminUsers.list(scopeKey(tenantID)), "all"],
    queryFn: () => fetchAllAdmins(tenantID),
  });
}

export function useAdminsQuery(tenantID: string | null) {
  return useQuery<PlatformAdminsListResponse, ApiError>({
    queryKey: queryKeys.adminUsers.list(scopeKey(tenantID)),
    queryFn: () => fetchAdmins(tenantID),
  });
}

// ── Mutations ────────────────────────────────────────────────────────

/**
 * Invite-admin mutation. Returns the full backend response
 * (admin_user + enrollment_token + optional warnings) so the dialog
 * can render the one-time enrollment-link reveal screen.
 *
 * Strips any empty string fields before submit — the backend's
 * CreateAdminUserRequest treats empty tenant_id / phone as "not set",
 * but serialising `""` would set them to an invalid value server-side.
 * On the tenant surface the body's tenant_id is ignored (the target
 * tenant is resolved from the path), so sending it is harmless.
 */
export function useInviteAdminMutation(tenantID: string | null) {
  const qc = useQueryClient();
  return useMutation<InviteAdminResponse, ApiError, InviteAdminInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(base(tenantID), {
          method: "POST",
          body: cleanInviteInput(input),
        }).then((raw) => inviteAdminResponseSchema.parse(raw)),
      ),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: queryKeys.adminUsers.all(scopeKey(tenantID)),
      });
    },
  });
}

function cleanInviteInput(input: InviteAdminInput): Record<string, unknown> {
  const out: Record<string, unknown> = {
    email: input.email,
    name: input.name,
    role: input.role,
  };
  if (input.phone && input.phone.trim() !== "") out.phone = input.phone.trim();
  if (input.tenant_id && input.tenant_id.trim() !== "")
    out.tenant_id = input.tenant_id.trim();
  return out;
}

/**
 * Change a tenant administrator's role. Tenant scope only: platform roles are
 * changed with the pureid-admin CLI, and the network API has no platform-scope
 * role route. The server requires tenant_admins.manage with a fresh step-up and
 * delegation authority over both the current and the new role.
 */
export function useUpdateAdminRoleMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<PlatformAdmin, ApiError, { adminId: string } & UpdatePlatformAdminRoleInput>({
    mutationFn: ({ adminId, role }) =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/${encodeURIComponent(adminId)}/role`, {
          method: "PATCH",
          body: { role },
        }).then((raw) => platformAdminSchema.parse(raw)),
      ),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: queryKeys.adminUsers.all(scopeKey(tenantID)),
      });
    },
  });
}

/**
 * Deactivate an admin. This replaced useRemoveAdminMutation, which issued
 * DELETE — an endpoint that no longer exists.
 *
 * The hard delete was unmounted because 70 foreign keys reference
 * admin_users, and on the success path three CASCADE and fifty-nine SET NULL:
 * granted_by, created_by, revoked_by, decided_by, attested_by and the rest
 * across the schema. The verb's only successful semantics was erasing the
 * attribution trail.
 *
 * The console never actually promised deletion. Its confirmation dialog said
 * "Revoke their admin access? They will be signed out of every active
 * session" — an accurate description of DEACTIVATION — while performing an
 * irreversible row delete, and demanded a typed-email confirmation that told
 * the operator this was serious without telling them what was serious. This
 * mutation is what the dialog always described.
 *
 * Server-side, deactivate ends sessions AND revokes the admin's API keys.
 */
export function useDeactivateAdminMutation(tenantID: string | null) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (adminId) =>
      withStepUp(() =>
        apiFetch<void>(
          `${base(tenantID)}/${encodeURIComponent(adminId)}/deactivate`,
          { method: "POST" },
        ),
      ),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: queryKeys.adminUsers.all(scopeKey(tenantID)),
      });
    },
  });
}

export function useResendInviteMutation(tenantID: string | null) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (adminId) =>
      withStepUp(() =>
        apiFetch<void>(
          `${base(tenantID)}/${encodeURIComponent(adminId)}/reissue-enrollment`,
          { method: "POST" },
        ),
      ),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: queryKeys.adminUsers.all(scopeKey(tenantID)),
      });
    },
  });
}
